import { supabase } from '../lib/supabase';

interface ImpersonationData {
  adminAccessToken: string;
  adminRefreshToken: string;
  targetBusinessId: string;
  targetBusinessName: string;
  targetUserEmail: string;
  timestamp: string;
}

const STORAGE_KEY = 'nexus_impersonation_data';

const BUSINESS_SCOPED_KEYS = [
  'selectedBusiness',
  'businesses',
  'preloadedCalendarData',
  'messages_conversation_open',
  'warrantyBookingData',
  'warrantyOriginalBookingId',
  'sales_map_open',
  'adding_selectable_service',
  'clock_widget_collapsed',
  'usePreloadedBusinessData',
  'skipInitializing',
  'preloadedRoute',
  'tutorialStep',
  'tutorialActive',
  'mobileNavConfig',
  'processed_reminder_ids',
  'aiInsights_reports',
  'aiInsights_sections',
  'aiInsights_dateRange',
  'calendar-behaviors',
  'useStockHistory',
];

function clearBusinessScopedStorage() {
  for (const key of BUSINESS_SCOPED_KEYS) {
    try { localStorage.removeItem(key); } catch {}
  }
}

export class ImpersonationService {
  static async startImpersonation(targetData: {
    ownerId: string;
    businessId: string;
    businessName: string;
    ownerEmail: string;
  }): Promise<{ success: boolean; message: string }> {
    if (!supabase) {
      return { success: false, message: 'Database connection not available' };
    }

    try {
      const { data: currentSession } = await supabase.auth.getSession();
      if (!currentSession?.session) {
        return { success: false, message: 'No current session found' };
      }

      const impersonationData: ImpersonationData = {
        adminAccessToken: currentSession.session.access_token,
        adminRefreshToken: currentSession.session.refresh_token,
        targetBusinessId: targetData.businessId,
        targetBusinessName: targetData.businessName,
        targetUserEmail: targetData.ownerEmail,
        timestamp: new Date().toISOString(),
      };

      localStorage.setItem(STORAGE_KEY, JSON.stringify(impersonationData));
      clearBusinessScopedStorage();

      const { data, error } = await supabase.functions.invoke('impersonate-user', {
        body: { targetUserId: targetData.ownerId },
      });

      if (error || !data?.hashed_token) {
        localStorage.removeItem(STORAGE_KEY);
        console.error('Edge function error:', error || 'No hashed_token returned');
        return { success: false, message: error?.message || 'Failed to generate impersonation token' };
      }

      const { error: otpError } = await supabase.auth.verifyOtp({
        token_hash: data.hashed_token,
        type: 'magiclink',
      });

      if (otpError) {
        localStorage.removeItem(STORAGE_KEY);
        console.error('OTP verification error:', otpError);
        return { success: false, message: 'Failed to switch session: ' + otpError.message };
      }

      window.location.href = '/';
      return { success: true, message: `Now impersonating ${targetData.ownerEmail}` };
    } catch (error) {
      localStorage.removeItem(STORAGE_KEY);
      console.error('Error starting impersonation:', error);
      return { success: false, message: 'Failed to start impersonation' };
    }
  }

  static async stopImpersonation(): Promise<{ success: boolean; message: string }> {
    try {
      const data = this.getImpersonationData();
      if (!data) {
        return { success: false, message: 'No active impersonation session' };
      }

      const { error } = await supabase.auth.setSession({
        access_token: data.adminAccessToken,
        refresh_token: data.adminRefreshToken,
      });

      localStorage.removeItem(STORAGE_KEY);
      clearBusinessScopedStorage();

      if (error) {
        console.error('Failed to restore admin session:', error);
        await supabase.auth.signOut();
        window.location.href = '/login';
        return { success: false, message: 'Session expired — please sign in again' };
      }

      window.location.href = '/super-admin';
      return { success: true, message: 'Returned to admin dashboard' };
    } catch (error) {
      localStorage.removeItem(STORAGE_KEY);
      console.error('Error stopping impersonation:', error);
      window.location.href = '/login';
      return { success: false, message: 'Failed to stop impersonation' };
    }
  }

  static isImpersonating(): boolean {
    return !!this.getImpersonationData();
  }

  static getImpersonationData(): ImpersonationData | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }
}
